import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SignaturePad from "../components/SignaturePad";
import Logo from "../components/Logo";
import PhotoCapture from "../components/PhotoCapture";
import { formatCpf, isValidCpf } from "../lib/cpf";
import { toast } from "sonner";
import { GraduationCap, FileText, ShieldCheck, Download, ExternalLink, CheckCircle2, Users, Clock, MapPin, Sparkles } from "lucide-react";

export default function PublicPortal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [enroll, setEnroll] = useState({ name: "", email: "", document: "", company: "", photo_data_url: "" });
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(null);
  const [consent, setConsent] = useState({ name: "", email: "", document: "" });
  const [sig, setSig] = useState("");
  const [consented, setConsented] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/public/portal/${slug}`);
    setData(r.data);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const doEnroll = async () => {
    if (!enroll.name.trim()) return toast.error("Nome é obrigatório");
    if (!/^\S+@\S+\.\S+$/.test(enroll.email)) return toast.error("Informe um e-mail válido");
    if (!isValidCpf(enroll.document)) return toast.error("CPF inválido");
    if (!enroll.photo_data_url) return toast.error("Envie sua foto para o crachá");
    if (enrolling) return;
    setEnrolling(true);
    try {
      const r = await api.post(`/public/portal/${slug}/enroll`, enroll);
      toast.success("Inscrição confirmada!");
      setEnrolled({ participant_id: r.data.participant_id, name: enroll.name, email: enroll.email });
      if (r.data.consent_enabled) {
        setConsent({ name: enroll.name, email: enroll.email, document: enroll.document });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao inscrever");
    } finally { setEnrolling(false); }
  };

  const submitConsent = async () => {
    if (!sig) return toast.error("Assine para autorizar");
    if (signing) return;
    setSigning(true);
    try {
      await api.post(`/public/portal/${slug}/consent`, {
        participant_id: enrolled?.participant_id,
        name: consent.name || enrolled?.name,
        email: consent.email || enrolled?.email,
        document: consent.document,
        signature_data_url: sig,
      });
      toast.success("Termo assinado com sucesso!");
      setConsented(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao assinar");
    } finally { setSigning(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center overline">Carregando…</div>;
  const brand = data.brand || {};
  const primary = brand.brand_primary || "#C07C88";
  const gold = brand.brand_accent || "#D4AF37";
  const t = data.training;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="flex items-center justify-between mb-6">
          <Logo size="md" />
          <p className="text-xs text-muted-foreground">Portal do Aluno</p>
        </div>

        <Card className="p-8 md:p-10 shadow-none border-border mb-6">
          <p className="overline">Treinamento</p>
          <h1 className="font-heading text-4xl mt-2" style={{ color: primary }}>{t.title}</h1>
          {t.instructor && <p className="text-sm text-muted-foreground mt-2">Instrutor: {t.instructor}</p>}
          <div className="flex flex-wrap gap-4 mt-6 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock size={13} /> {t.hours || 0}h</span>
            <span className="inline-flex items-center gap-1 uppercase tracking-wider">{t.modality}</span>
            {t.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {t.location}</span>}
            {t.starts_at && <span>Início: {t.starts_at?.slice(0,10)}</span>}
          </div>
          {t.scope && (
            <div className="mt-6 pt-6 border-t border-border">
              <p className="overline mb-2">Escopo</p>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{t.scope}</p>
            </div>
          )}
        </Card>

        <Tabs defaultValue={data.enrollment_open ? "enroll" : (data.materials_public ? "materials" : "consent")} className="w-full">
          <TabsList className="bg-white border border-border">
            {data.enrollment_open && <TabsTrigger value="enroll" data-testid="tab-enroll"><Users size={13} className="mr-1" /> Inscrição</TabsTrigger>}
            {data.materials_public && <TabsTrigger value="materials" data-testid="tab-materials"><FileText size={13} className="mr-1" /> Materiais</TabsTrigger>}
            {data.consent_enabled && <TabsTrigger value="consent" data-testid="tab-consent"><ShieldCheck size={13} className="mr-1" /> Termo Imagem/Voz</TabsTrigger>}
          </TabsList>

          {data.enrollment_open && (
            <TabsContent value="enroll">
              <Card className="p-6 md:p-8 shadow-none border-border">
                {enrolled ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: `${gold}15` }}>
                      <CheckCircle2 size={32} className="text-emerald-600" />
                    </div>
                    <p className="overline mt-4 text-emerald-700">Inscrição confirmada</p>
                    <p className="font-heading text-2xl mt-2">{enrolled.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{enrolled.email}</p>
                    <p className="text-xs text-muted-foreground mt-4">Seu crachá com QR Code será entregue no primeiro dia. Apresente-o na entrada para registrar sua presença diária.</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="overline">Inscreva-se</p>
                        <p className="text-xs text-muted-foreground mt-1">{data.enrolled_count} inscritos{data.vacancies_left != null ? ` · ${data.vacancies_left} vagas restantes` : ""}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="md:col-span-2 mb-2"><Label className="mb-2 block">Foto para o crachá *</Label><PhotoCapture value={enroll.photo_data_url} onChange={(v) => setEnroll((s) => ({ ...s, photo_data_url: v }))} color={primary} /></div>
                      <div className="md:col-span-2"><Label>Nome completo *</Label><Input value={enroll.name} onChange={(e) => setEnroll({ ...enroll, name: e.target.value })} data-testid="enroll-name" /></div>
                      <div><Label>E-mail *</Label><Input type="email" value={enroll.email} onChange={(e) => setEnroll({ ...enroll, email: e.target.value })} data-testid="enroll-email" /></div>
                      <div><Label>CPF *</Label><Input inputMode="numeric" placeholder="000.000.000-00" value={enroll.document} onChange={(e) => setEnroll({ ...enroll, document: formatCpf(e.target.value) })} data-testid="enroll-document" />{enroll.document.length === 14 && !isValidCpf(enroll.document) && <p className="text-xs text-red-500 mt-1" data-testid="enroll-cpf-error">CPF inválido</p>}</div>
                      <div className="md:col-span-2"><Label>Empresa</Label><Input value={enroll.company} onChange={(e) => setEnroll({ ...enroll, company: e.target.value })} data-testid="enroll-company" /></div>
                    </div>
                    <Button className="w-full mt-4 h-12" style={{ background: primary, color: "#fff" }} onClick={doEnroll} disabled={enrolling} data-testid="enroll-submit">
                      {enrolling ? "Confirmando…" : "Confirmar inscrição"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Sparkles size={12} /> A vaga é reservada por ordem de chegada.</p>
                  </>
                )}
              </Card>
            </TabsContent>
          )}

          {data.materials_public && (
            <TabsContent value="materials">
              <Card className="p-6 shadow-none border-border">
                <p className="overline mb-4">Biblioteca do Aluno ({data.materials.length})</p>
                {data.materials.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Nenhum material disponível ainda.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {data.materials.map((m) => (
                      <div key={m.material_id} className="py-3 flex items-center gap-3" data-testid={`material-${m.material_id}`}>
                        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${primary}10`, color: primary }}>
                          <FileText size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{m.name}</p>
                          {m.description && <p className="text-xs text-muted-foreground truncate">{m.description}</p>}
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{m.kind === "file" ? "Arquivo" : "Link externo"}</p>
                        </div>
                        <Button size="sm" variant="outline" asChild data-testid={`material-open-${m.material_id}`}>
                          <a href={m.url_or_data} target="_blank" rel="noreferrer" download={m.kind === "file" ? m.name : undefined}>
                            {m.kind === "file" ? <><Download size={13} className="mr-1" /> Baixar</> : <><ExternalLink size={13} className="mr-1" /> Abrir</>}
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {data.consent_enabled && (
            <TabsContent value="consent">
              <Card className="p-6 md:p-8 shadow-none border-border">
                {consented ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: `${gold}15` }}>
                      <ShieldCheck size={32} style={{ color: gold }} />
                    </div>
                    <p className="overline mt-4" style={{ color: gold }}>Autorização registrada</p>
                    <p className="font-heading text-2xl mt-2">Obrigado, {consent.name || enrolled?.name}!</p>
                    <p className="text-sm text-muted-foreground mt-2">Trilha de auditoria salva. Você poderá revogar por escrito a qualquer momento.</p>
                  </div>
                ) : (
                  <>
                    <p className="overline mb-3">Termo de Uso de Imagem e Voz</p>
                    <div className="doc-paper p-5 max-h-64 overflow-auto text-sm whitespace-pre-wrap leading-relaxed" data-testid="consent-text">
                      {data.consent_text}
                    </div>
                    <div className="grid md:grid-cols-3 gap-3 mt-4">
                      <div><Label>Nome</Label><Input value={consent.name} onChange={(e) => setConsent({ ...consent, name: e.target.value })} data-testid="consent-name" /></div>
                      <div><Label>E-mail</Label><Input value={consent.email} onChange={(e) => setConsent({ ...consent, email: e.target.value })} data-testid="consent-email" /></div>
                      <div><Label>CPF</Label><Input value={consent.document} onChange={(e) => setConsent({ ...consent, document: e.target.value })} data-testid="consent-document" /></div>
                    </div>
                    <div className="mt-4">
                      <Label className="mb-2 block">Assinatura</Label>
                      <SignaturePad onChange={setSig} testid="consent-signature" />
                    </div>
                    <Button className="w-full mt-4 h-12" style={{ background: primary, color: "#fff" }} onClick={submitConsent} disabled={signing} data-testid="consent-submit">
                      {signing ? "Registrando…" : "Autorizar uso de imagem/voz"}
                    </Button>
                  </>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>

        <p className="text-center text-xs text-muted-foreground mt-8">{brand.company_name || "Leadera Corp"}</p>
      </div>
    </div>
  );
}
